// Home › Needs you, Blueprint only (pages/ux-full screens/home.js, #/home), on DashboardPage (BL-189): a
// greeting, the figures, and what needs the person beside the month against the goal and what the agents
// did. Every section is a ui-web view drawn from its own tool's answer, the same view an AI tool's chat
// draws, so the office and the chat never disagree. A section shows only to whoever may call its tool
// (home-model.ts), and says its own loading and failure (the pattern's). Under Needs you, an owner or
// manager with suggested ad changes waiting is told so (adChanges.list). Beside the greeting, The month
// so far and, for whoever reads it, the price book (places.ts' opensPricebook).
import { dateText, timeParts } from '@flowology/kernel';
import { AgentActivityProps, viewFor, type OpenTarget, type ToolName } from '@flowology/ui-web';
import { AgentActivity, Button, Figures, Link } from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useQuery } from '../books/ui.tsx';
import { mayDecideAdChanges, waitingChangesLine } from './ads-model.ts';
import {
  greeting,
  homeFigures,
  homeSections,
  homeTools,
  type HomeData,
  type HomeSection,
} from './home-model.ts';
import { officeHost } from './host.ts';
import { opensPricebook, PRICEBOOK_PAGE } from './places.ts';
import './home-page.css';

const TOOL: Record<HomeSection, ToolName> = {
  approvals: 'approvals.list',
  pace: 'gp.pace',
  activity: 'agents.activity',
  aging: 'receivables.aging',
};
const INPUT: Record<HomeSection, unknown> = {
  approvals: { status: 'pending' },
  pace: {},
  activity: {},
  aging: {},
};
const TITLE: Record<HomeSection, string> = {
  approvals: 'Needs you',
  pace: 'The month against the goal',
  activity: 'What the agents did today',
  aging: 'Who owes you',
};
const WHAT: Record<HomeSection, string> = {
  approvals: 'what waits for your OK',
  pace: 'the month',
  activity: 'what the agents did',
  aging: 'who owes you',
};
/** Where a figure opens: the page its section stands for (the agents' own page for what they did). */
const OPENS: Record<HomeSection, OpenTarget | 'agents'> = {
  approvals: { kind: 'approvals' },
  pace: { kind: 'numbers' },
  activity: 'agents',
  aging: { kind: 'owed' },
};

export function HomeScreen({
  nav,
  held,
  onOpen,
  opens,
}: {
  nav: NavProps;
  held: readonly string[];
  /** Opens a place from a view or a figure (App.tsx). */
  onOpen: (t: OpenTarget) => void;
  /** Whether this person may open it: a figure that can't is only a figure. */
  opens: (t: OpenTarget) => boolean;
}) {
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tools = homeTools(held);
  const sees = (s: HomeSection) => tools.includes(s);
  // a tool this person may not call is never asked (null waits), so nothing is refused
  const q: Record<HomeSection, ReturnType<typeof useQuery<unknown>>> = {
    approvals: useQuery<unknown>(TOOL.approvals, sees('approvals') ? INPUT.approvals : null),
    pace: useQuery<unknown>(TOOL.pace, sees('pace') ? INPUT.pace : null),
    activity: useQuery<unknown>(TOOL.activity, sees('activity') ? INPUT.activity : null),
    aging: useQuery<unknown>(TOOL.aging, sees('aging') ? INPUT.aging : null),
  };
  // suggested ad changes waiting: only those who decide them (marketing.manage) ask
  const changes = useQuery<{ waiting: number }>(
    'adChanges.list',
    mayDecideAdChanges(held) ? { status: ['waiting'], limit: 1 } : null,
  );
  const changesLine = waitingChangesLine(changes.data?.waiting ?? 0);
  const host = officeHost(onOpen);
  const crumb = usePageCrumb(nav);
  // What the agents did: the section names it, so the view's own title isn't drawn twice
  const drawn = (s: HomeSection) => {
    const data = q[s].data;
    if (data == null) return null;
    if (s !== 'activity') return viewFor(TOOL[s], data, host);
    const p = AgentActivityProps.safeParse(data);
    return p.success ? <AgentActivity data={p.data} host={host} titled={false} /> : null;
  };
  // the figures read the same answers the views do, once each parses as its view's props
  const data: HomeData = Object.fromEntries(
    tools.filter((s) => drawn(s)).map((s) => [s, q[s].data]),
  ) as HomeData;
  const now = new Date();
  const hour = Number(
    new Intl.DateTimeFormat('en-CA', { hour: 'numeric', hourCycle: 'h23', timeZone: tz }).format(now),
  );
  const clock = timeParts(now, tz);
  const sections = homeSections(held);
  const section = (s: HomeSection): DashboardSection => {
    const view = drawn(s);
    const base = { id: s, title: TITLE[s], what: WHAT[s] };
    if (q[s].data == null)
      return q[s].error
        ? { ...base, status: 'failed', failedText: q[s].error, onRetry: () => void q[s].reload() }
        : { ...base, status: 'loading', onRetry: () => void q[s].reload() };
    if (!view) return { ...base, status: 'empty', emptyText: `Flowology can’t show ${WHAT[s]} here.` };
    return {
      ...base,
      children: (
        <>
          {view}
          {s === 'approvals' && changesLine && (
            <p className="bp-home-adc">
              {changesLine} <Link onPress={() => nav.onView('marketing')}>Open How the ads did</Link>
            </p>
          )}
        </>
      ),
    };
  };
  const figures = homeFigures(data).map((f) => {
    const to = OPENS[f.id];
    if (to === 'agents') return { ...f, onOpen: () => nav.onView('agents') };
    return opens(to) ? { ...f, onOpen: () => onOpen(to) } : f;
  });
  const actions = [
    opens({ kind: 'numbers' }) && (
      <Button key="numbers" onPress={() => onOpen({ kind: 'numbers' })}>
        The month so far
      </Button>
    ),
    // the price book, a step from Home for whoever reads it (Amir, 26 Oct): Setup's first page
    opensPricebook(held) && (
      <Button key="pricebook" onPress={() => nav.onView(PRICEBOOK_PAGE.view, PRICEBOOK_PAGE.part)}>
        Price book
      </Button>
    ),
  ].filter(Boolean);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: greeting(hour),
            lede: `${dateText(now, tz, { weekday: 'long', year: false })}, ${clock.time} ${clock.suffix} Here’s what needs you.`,
            ...(actions.length ? { actions: <>{actions}</> } : {}),
            ...(figures.length ? { figures: <Figures items={figures} /> } : {}),
          }}
          main={sections.slice(0, 1).map(section)}
          side={sections.slice(1).map(section)}
        />
      </main>
    </div>
  );
}
